// UserEdit.js
import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router-dom'
import {
	Button,
	ConfigProvider,
	Form,
	Input,
	Tooltip,
	Row,
	Col,
	DatePicker,
	Typography,
	Tag
} from 'antd'
import PropTypes from 'prop-types'
import { runInAction, toJS } from 'mobx'
import { IS_NEW } from '../../../config'
import { actSaveUser } from '../../../stores/users/actions'
import { useStores } from '../../../cls/stores/MobXProvider'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import PrewritePlatform from '../../../components/Shared/PrewritePlatform'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import LstTypeInputBase from '../../../components/Form/LstTypeInputBase'
import { Platforms } from '../../../cls/utils/platforms'
import { SelectUsers } from '../../../components/Form/SelectUsers'
import PermissionsInput from '../../../components/Form/PermissionsInput'
import { BusinessDetailsInput } from '../../../components/Form/BusinessDetailsInput'
import UserPreferencesInput from '../../../components/Form/UserPreferencesInput'
import { requiredVersion } from '../../../config/versionUtils'
import { useSmartTranslation } from '../../../cls/TranslationContext'
import AiAssistantWpPublicChatPanel from './helpers/AiAssistantWpPublicChatPanel'
const { RangePicker } = DatePicker
const { Paragraph } = Typography

// ---------------------------------------------------------------------------
// Custom SectionBlock Component
// ---------------------------------------------------------------------------
/**
 * SectionBlock
 *
 * A reusable UI component to render collapsible sections inside the user form.
 *
 * @param {Object} props
 * @param {string} props.id - The unique DOM id for the section.
 * @param {string} props.label - The label/title of the section.
 * @param {string} props.sectionKey - Key used to toggle open/close state.
 * @param {boolean} props.isOpen - Boolean indicating if the section is expanded.
 * @param {function} props.toggleSection - Function to handle toggling the section open/close.
 * @param {JSX.Element} props.children - Child components/content inside the section.
 */
const SectionBlock = ({
	id,
	label,
	sectionKey,
	isOpen,
	toggleSection,
	children
}) => {
	return (
		<div className="section-block" id={id}>
			<div
				className="section-block-header"
				onClick={() => toggleSection(sectionKey)}
			>
				<span>{label}</span>
				<span>{isOpen ? '-' : '+'}</span>
			</div>
			<div
				className="section-block-content"
				style={{ display: isOpen ? 'block' : 'none' }}
			>
				{children}
			</div>
		</div>
	)
}

SectionBlock.propTypes = {
	id: PropTypes.string.isRequired,
	label: PropTypes.node.isRequired,
	sectionKey: PropTypes.string.isRequired,
	isOpen: PropTypes.bool.isRequired,
	toggleSection: PropTypes.func.isRequired,
	children: PropTypes.node
}

// ---------------------------------------------------------------------------
// UserEdit Component
// ---------------------------------------------------------------------------
/**
 * Profile component allows viewing and editing of user profile information.
 * It handles both new user creation and existing user profile updates.
 *
 * @component
 * @returns {JSX.Element} The rendered UserEdit component.
 */
const UserEdit = ({ viewOnly = false }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const queryParams = new URLSearchParams(window.location.search)
	const tabParam = queryParams.get('tab')
	const sectionParam = queryParams.get('section')
	const [form] = Form.useForm()
	const { itmId } = useParams()
	const { users, tourStore, categories } = useStores()
	const { loggedInUser } = users
	const isSuperAdmin = loggedInUser.isSuperAdmin
	const isAdmin = loggedInUser.isAdmin
	const user = users.requireUserById(itmId)
	const formIsDisabled = viewOnly

	const [activeTab, setActiveTab] = useState('details')
	const [expandedKeys, setExpandedKeys] = useState([])

	// Toggle section expansion state (affects display only)
	/**
	 * toggleSection
	 *
	 * Toggles the visibility of a section in the form.
	 * Adds or removes the given key from the `expandedKeys` array.
	 *
	 * @param {string} sectionKey - Key representing the section to toggle.
	 */
	const toggleSection = (sectionKey) => {
		setExpandedKeys((prev) =>
			prev.includes(sectionKey)
				? prev.filter((key) => key !== sectionKey)
				: [...prev, sectionKey]
		)
	}

	/**
	 * generateInitialFormValues
	 *
	 * Generates the default values to initialize the user form based on the current user data.
	 *
	 * @returns {Object} The initial form values mapped to field names.
	 */
	const generateInitialFormValues = () => ({
		name: user.name,
		platforms: user.platforms,
		clients: user.clients,
		team: user.team,
		allowed: user.allowed,
		// Contract
		contractMonthlyHours: user.contractMonthlyHours,
		contractNumber: user.contractNumber,
		contractDate: user.contractDate,
		// Business
		business_name: user.businessName,
		business_id: user.businessId,
		timezone: user.businessDetails?.timezone,
		// Address
		country: user.businessDetails?.address?.country || '',
		state: user.businessDetails?.address?.state || '',
		city: user.businessDetails?.address?.city || '',
		zip_code: user.businessDetails?.address?.zip_code || '',
		address_line_1: user.businessDetails?.address?.address_line_1 || '',
		address_line_2: user.businessDetails?.address?.address_line_2 || '',
		// Phone
		prefix: user.businessDetails?.mobile?.prefix || '',
		number: user.businessDetails?.mobile?.number || '',
		status: user.businessDetails?.mobile?.status === 'verified',

		// Ai assistant public chat
		ai_wp_chat_site_url: user.aiWpChat?.site_url || '',
		ai_wp_chat_openai_api_key: user.aiWpChat?.openai_api_key || '',
		ai_wp_chat_openai_validated_at:
			user.aiWpChat?.openai_validated_at || null,
		ai_wp_chat_openai_validated_for:
			user.aiWpChat?.openai_validated_for || '',
		ai_wp_chat_api_token: user.aiWpChat?.token || '',
		ai_wp_chat_subscription_id: user.aiWpChat?.subscription_id || ''
	})

	useEffect(() => {
		if (tabParam && activeTab !== tabParam) {
			setActiveTab(tabParam)
		}
		if (sectionParam && expandedKeys[0] !== sectionParam) {
			setExpandedKeys([sectionParam])
		}
		if (tabParam || sectionParam) {
			const url = new URL(window.location.href)
			url.searchParams.delete('tab')
			url.searchParams.delete('section')
			window.history.replaceState({}, document.title, url)
		}
		form.setFieldsValue(generateInitialFormValues())
	}, [itmId])

	useEffect(() => {
		form.setFieldsValue(generateInitialFormValues())
	}, [user.modified])

	const lstPermissions = ['read', 'write', 'delete', 'delete_view']
	const categoryPermissions = ['start', 'end', 'demo']
	const rolesAndResourcePermissions = ['status']
	const notificationPermissions = ['show', 'email']

	// Base allowed object for a user
	const roles = {
		superAdmin: false,
		admin: false,
		clientAdmin: false,
		client: false,
		teamAdmin: false,
		team: false
	}

	const lst = {
		sprints: {
			read: true,
			write: false,
			delete: false,
			delete_view: false
		},
		sprints_team: {
			read: true,
			write: false,
			delete: false,
			delete_view: false
		},
		requests: {
			read: true,
			write: false,
			delete: false,
			delete_view: false
		},
		meetings: {
			read: true,
			write: false,
			delete: false,
			delete_view: false
		},
		monthly_reports: {
			read: true,
			write: false,
			delete: false,
			delete_view: false
		},
		notifications: {
			read: true,
			write: false,
			delete: false,
			delete_view: false
		},
		documentation: {
			read: true,
			write: false,
			delete: false,
			delete_view: false
		},
		users: { read: true, write: false, delete: false, delete_view: false },
		recruitment: {
			read: true,
			write: false,
			delete: false,
			delete_view: false
		},
		secured: {
			read: true,
			write: false,
			delete: false,
			delete_view: false
		},
		workflows: {
			read: true,
			write: false,
			delete: false,
			delete_view: false
		}
		// ...other lst permissions
	}

	const expandedCategories = Object.keys(categories).reduce((acc, key) => {
		acc[key] = {
			demo: false,
			start: '',
			end: ''
		}
		return acc
	}, {})

	const resources = {
		switchApp: false,
		videoMeeting: false,
		upload_photos: false,
		upload_files: false,
		chat: false,
		ai: false
	}

	const notifications = {
		sprints: {
			new: { show: false, mail: false },
			update: { show: false, mail: false },
			progress: { show: false, mail: false }
		},
		sprints_team: {
			new: { show: false, mail: false },
			update: { show: false, mail: false },
			progress: { show: false, mail: false }
		},
		requests: {
			new: { show: false, mail: false },
			update: { show: false, mail: false },
			progress: { show: false, mail: false }
		}
	}

	// Check if editing a super admin user
	const isEditingSuperAdmin = Form.useWatch(['allowed', 'superAdmin'], form)

	/**
	 * Handles form submission for saving user profile.
	 *
	 * @param {Object} vals - The form values.
	 */
	const _doSave = (vals) => {
		if (vals !== null) {
			// vals.platforms = ensureEncryptedPasswords(vals.platforms, loggedInUserId)
			runInAction(() => {
				if (user.loaded === IS_NEW) user.resetLastFullLoad()
				user.setName(vals.name)
				user.setPlatforms(vals.platforms)
				user.setClients(vals.clients)
				user.setTeam(vals.team)

				user.setBusinessDetails(vals)
				user.setAllowed(vals.allowed)
				user.setContractMonthlyHours(vals.contractMonthlyHours)
				user.setContractNumber(vals.contractNumber)
				user.setContractDate(
					vals.contractDate
						? vals.contractDate.map((date) => date.unix())
						: []
				)
				user.updateAiWpChatToken({
					site_url: vals.ai_wp_chat_site_url,
					openai_api_key: vals.ai_wp_chat_openai_api_key,
					openai_validated_at: vals.ai_wp_chat_openai_validated_at,
					openai_validated_for: vals.ai_wp_chat_openai_validated_for,
					token: vals.ai_wp_chat_api_token
				})
			})
			actSaveUser(user.user_id, toJS(user.full), toJS(user.allowed)).then(
				() => {
					users.saveState()
					navigate('/dashboard')
				}
			)
		}
	}

	/**
	 * startTour
	 *
	 * Starts the global user profile tour. Builds the tour steps dynamically based on permissions and app version.
	 */
	const startTour = () => {
		const activeTourKeys = [
			'profile',
			'buttonDetails',
			'buttonPlatforms',
			requiredVersion('1.5.0') && 'buttonPreferences',
			'buttonPermissions',
			isSuperAdmin && 'buttonAdmin'
		].filter((key) => key !== undefined)
		const tourProfile = getTourArrayByKeys('userProfile', activeTourKeys)
		tourStore.startTour('User profile tour', [...tourProfile])
	}

	/**
	 * startTourDetails
	 *
	 * Initiates a guided tour for the "Details" tab and opens key sections.
	 */
	const startTourDetails = () => {
		const userDetails = getTourArrayByKeys('userDetails', ['all'])
		tourStore.startTour('userDetails-tour', [...userDetails])
		setExpandedKeys([
			'section-general',
			'section-business',
			'section-contact'
		])
	}

	/**
	 * startTourPlatforms
	 *
	 * Initiates a guided tour for the "Platforms" tab and expands the platforms section.
	 */
	const tourPlatformsTab = getTourArrayByKeys('userPlatforms', ['all'])
	const startTourPlatforms = () => {
		tourStore.startTour('UserPlatformsTab-tour', [...tourPlatformsTab])
		setExpandedKeys(['section-platforms'])
	}

	/**
	 * startTourPreferences
	 *
	 * Starts the tour for the "Preferences" tab and opens the preference section.
	 */
	const userPreferencesTab = getTourArrayByKeys('userPreferences', ['all'])
	const startTourPreferences = () => {
		tourStore.startTour('userPreferences-tour', [...userPreferencesTab])
		setExpandedKeys(['1'])
	}

	/**
	 * startTourPermissions
	 *
	 * Starts the tour for the "Permissions" tab and opens the notifications section.
	 */
	const userPermissions = getTourArrayByKeys('userPermissions', ['all'])
	const startTourPermissions = () => {
		tourStore.startTour('userPermissions-tour', [...userPermissions])
		setExpandedKeys(['section-notifications'])
	}

	/**
	 * startTourAdmin
	 *
	 * Starts the tour for the "Admin" tab and opens all admin-specific permission sections.
	 */
	const userAdminSection = getTourArrayByKeys('userAdminSection', ['all'])
	const startTourAdmin = () => {
		tourStore.startTour('UserAdminTab-tour', [...userAdminSection])
		setExpandedKeys([
			'section-roles',
			'section-permissions',
			'section-categories',
			'section-resources'
		])
	}

	useEffect(() => {
		if (!tourStore.open && !(tabParam || sectionParam)) {
			setExpandedKeys([])
		}
	}, [tourStore.open])

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				buttonProps={{ onClick: startTour }}
				id="target-user-edit-text"
				titleProps={{
					text: t('single.profile'),
					children: (
						<span id="target-info-name">
							<Tag
								style={{
									fontSize: 'inherit',
									fontWeight: 'normal',
									padding: '8px',
									marginLeft: '8px'
								}}
							>
								{(() => {
									if (viewOnly) {
										return (
											<Tooltip title={t('__view%_user')}>
												{user.name}
											</Tooltip>
										)
									} else if (user.loaded === IS_NEW) {
										return t('__create-new%_user')
									} else {
										return (
											<Tooltip
												title={t('__edit%_user.single')}
											>
												{user.name}
											</Tooltip>
										)
									}
								})()}
							</Tag>
						</span>
					)
				}}
			/>
			<p>{t('single.profile-page.description')}</p>
			<ConfigProvider>
				<Form
					labelCol={{ span: 6 }}
					wrapperCol={{ span: 18 }}
					form={form}
					name="profile"
					initialValues={generateInitialFormValues()}
					onFinish={_doSave}
					className={viewOnly && 'view-only-styles'}
				>
					{/* Tabs */}
					<Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
						{/* 1. Details */}
						<Col>
							<Button
								id="target-button-details"
								type={
									activeTab === 'details'
										? 'primary'
										: 'default'
								}
								onClick={() => setActiveTab('details')}
							>
								{t('single.details')}
							</Button>
						</Col>
						{/* 2. Platforms */}
						<Col>
							<Button
								id="target-button-platforms"
								type={
									activeTab === 'platforms'
										? 'primary'
										: 'default'
								}
								onClick={() => setActiveTab('platforms')}
							>
								{t('_platform.multiple')}
							</Button>
						</Col>
						{/* 3. Preferences */}
						{requiredVersion('1.5.0') && (
							<Col>
								<Button
									id="target-button-preferences"
									type={
										activeTab === 'preferences'
											? 'primary'
											: 'default'
									}
									onClick={() => setActiveTab('preferences')}
								>
									{t('single.preferences')}
								</Button>
							</Col>
						)}
						{/* 4. Permissions */}
						{isSuperAdmin && (
							<Col>
								<Button
									id="target-button-permissions"
									type={
										activeTab === 'permissions'
											? 'primary'
											: 'default'
									}
									onClick={() => setActiveTab('permissions')}
								>
									{t('_permision.multiple')}
								</Button>
							</Col>
						)}
						{/* 5. Admin */}
						{isSuperAdmin && (
							<Col>
								<Button
									id="target-button-admin"
									type={
										activeTab === 'admin'
											? 'primary'
											: 'default'
									}
									onClick={() => setActiveTab('admin')}
								>
									{t('single.admin')}
								</Button>
							</Col>
						)}
					</Row>
					{/* Tab Content */}
					{/* 1. Details */}

					<div
						style={{
							display: activeTab === 'details' ? 'block' : 'none'
						}}
					>
						<GeneralTourTitle
							titleProps={{
								h: 'p',
								text: t('__tour%single.details')
							}}
							buttonProps={{ onClick: startTourDetails }}
						/>
						<div style={{ display: 'block' }}>
							<SectionBlock
								id="target-section-general"
								label={t('single.general-settings')}
								sectionKey="section-general"
								isOpen={expandedKeys.includes(
									'section-general'
								)}
								toggleSection={toggleSection}
							>
								<Row>
									<Col flex="auto">
										{user.discount && (
											<Form.Item
												label={t('single.discount')}
												tooltip={t('single.discount')}
											>
												{user.discount}
											</Form.Item>
										)}
										{user.getActivePromoCodesString && (
											<Form.Item
												label={t('single.seller-code')}
												tooltip={t(
													'single.seller-code'
												)}
											>
												{user.getActivePromoCodesString}
											</Form.Item>
										)}
										<Form.Item
											label={t('single.email')}
											tooltip={t(
												'single.email-name-app-usage'
											)}
										>
											{user.email}
										</Form.Item>
										<Form.Item
											label={t('single.name')}
											tooltip={t(
												'single.email-name-app-usage'
											)}
											name="name"
											rules={[
												{
													required: true,
													message: t(
														'__provide.please%single.name'
													)
												}
											]}
										>
											<Input disabled={formIsDisabled} />
										</Form.Item>
										{isAdmin && (
											<>
												<Form.Item
													label={t(
														'_client.multiple'
													)}
													tooltip={t(
														'_client.tooltip'
													)}
													name="clients"
												>
													<SelectUsers
														placeholder={t(
															'_client.multiple'
														)}
														type="clients"
														mode="multiple"
														style={{
															width: '100%'
														}}
														disabled={
															formIsDisabled
														}
													/>
												</Form.Item>
												<Form.Item
													label={t(
														'single.team.label'
													)}
													tooltip={t(
														'_team-member.tooltip'
													)}
													name="team"
												>
													<SelectUsers
														placeholder={t(
															'single.team.label'
														)}
														type="team"
														mode="multiple"
														style={{
															width: '100%'
														}}
														disabled={
															formIsDisabled
														}
													/>
												</Form.Item>
											</>
										)}
									</Col>
								</Row>
							</SectionBlock>
							<SectionBlock
								id="target-section-business"
								label={t('single.business-details.label')}
								sectionKey="section-business"
								isOpen={expandedKeys.includes(
									'section-business'
								)}
								toggleSection={toggleSection}
							>
								<BusinessDetailsInput
									formIsDisabled={formIsDisabled}
									form={form}
								/>
							</SectionBlock>
							<SectionBlock
								id="target-section-contact"
								label={t('single.contract.details.title')}
								sectionKey="section-contact"
								isOpen={expandedKeys.includes(
									'section-contact'
								)}
								toggleSection={toggleSection}
							>
								<Row>
									<Col span={24}>
										<Paragraph style={{ color: '#1890ff' }}>
											{t(
												'single.contract.details.completed-by'
											)}
										</Paragraph>
									</Col>
									<Col span={24}>
										<Form.Item
											label={t('single.contract.nr')}
											tooltip={t('single.contract.nr')}
											name="contractNumber"
										>
											<Input
												placeholder={t(
													'single.contract.nr'
												)}
												disabled={
													!isSuperAdmin ||
													formIsDisabled
												}
											/>
										</Form.Item>
									</Col>
									<Col span={24}>
										<Form.Item
											label={t('single.period')}
											tooltip={t('single.period')}
											name="contractDate"
										>
											<RangePicker
												placeholder={[
													t('single.start-date'),
													t('single.end-date')
												]}
												style={{ width: '100%' }}
												disabled={
													!isSuperAdmin ||
													formIsDisabled
												}
											/>
										</Form.Item>
									</Col>
									{loggedInUser.isTeam && (
										<Col span={24}>
											<Form.Item
												label={t(
													'single.contract.guaranteed-hours'
												)}
												tooltip={t(
													'single.contract.guaranteed-hours'
												)}
												name="contractMonthlyHours"
											>
												<Input
													placeholder={t(
														'single.contract.guaranteed-hours'
													)}
													disabled={
														!isSuperAdmin ||
														formIsDisabled
													}
												/>
											</Form.Item>
										</Col>
									)}
								</Row>
							</SectionBlock>
						</div>
					</div>
					{/* 2. Platforms */}
					<div
						style={{
							display:
								activeTab === 'platforms' ? 'block' : 'none'
						}}
					>
						<GeneralTourTitle
							titleProps={{
								h: 'p',
								text: t('__tour%_platform.multiple')
							}}
							buttonProps={{ onClick: startTourPlatforms }}
						/>
						<div style={{ display: 'block' }}>
							<SectionBlock
								id="target-section-platforms"
								label={t('__manage%_platform.multiple')}
								sectionKey="section-platforms"
								isOpen={expandedKeys.includes(
									'section-platforms'
								)}
								toggleSection={toggleSection}
							>
								<Row>
									<Col flex="auto">
										<Form.Item
											label={t('_platform.multiple')}
											tooltip={t(
												'_platform.instructions'
											)}
											name="platforms"
										>
											<LstTypeInputBase
												disabled={formIsDisabled}
												UseLstClass={Platforms}
												phaseTwoLimited
												additionalEditSection={(
													viewOnly,
													form
												) => (
													<PrewritePlatform
														form={form}
														viewOnly={viewOnly}
													/>
												)}
											/>
										</Form.Item>
									</Col>
								</Row>
							</SectionBlock>
							<SectionBlock
								id="target-section-ai-assistant-public-chat"
								label="Ai assistant public chat"
								sectionKey="ai-assistant-public-chat"
								isOpen={expandedKeys.includes(
									'ai-assistant-public-chat'
								)}
								toggleSection={toggleSection}
							>
								<AiAssistantWpPublicChatPanel
									form={form}
									disabled={formIsDisabled}
								/>
							</SectionBlock>
						</div>
					</div>

					{/* 3. Preferences */}
					<div
						style={{
							display:
								activeTab === 'preferences' ? 'block' : 'none'
						}}
					>
						<GeneralTourTitle
							titleProps={{
								h: 'p',
								text: t('__tour%_preference.multiple')
							}}
							buttonProps={{ onClick: startTourPreferences }}
						/>
						<div style={{ display: 'block' }}>
							<SectionBlock
								id="target-preferences-tab"
								label={t('_sprint.filter.preferences')}
								sectionKey="1"
								isOpen={expandedKeys.includes('1')}
								toggleSection={toggleSection}
							>
								<UserPreferencesInput
									user={user}
									formIsDisabled={formIsDisabled}
								/>
							</SectionBlock>
						</div>
					</div>

					{/* 4. Permissions */}
					<div
						style={{
							display:
								activeTab === 'permissions' ? 'block' : 'none'
						}}
					>
						<GeneralTourTitle
							titleProps={{
								h: 'p',
								text: t('__tour%_permision.multiple')
							}}
							buttonProps={{ onClick: startTourPermissions }}
						/>
						<div style={{ display: 'block' }}>
							<SectionBlock
								id="target-section-notifications"
								label={t('__manage%_notification.multiple')}
								sectionKey="section-notifications"
								isOpen={expandedKeys.includes(
									'section-notifications'
								)}
								toggleSection={toggleSection}
							>
								<PermissionsInput
									data={notifications}
									permissions={notificationPermissions}
									type="notifications"
									formIsDisabled={formIsDisabled}
									form={form}
								/>
							</SectionBlock>
						</div>
					</div>

					{/* 5. Admin */}
					<div
						style={{
							display: activeTab === 'admin' ? 'block' : 'none'
						}}
					>
						<GeneralTourTitle
							titleProps={{
								h: 'p',
								text: t('__tour%single.admin')
							}}
							buttonProps={{ onClick: startTourAdmin }}
						/>
						<div style={{ display: 'block' }}>
							<SectionBlock
								id="target-section-roles"
								label={t('__manage%_role.multiple')}
								sectionKey="section-roles"
								isOpen={expandedKeys.includes('section-roles')}
								toggleSection={toggleSection}
							>
								<PermissionsInput
									data={roles}
									permissions={rolesAndResourcePermissions}
									type="roles"
									formIsDisabled={
										formIsDisabled ||
										!(isEditingSuperAdmin || isAdmin)
									}
								/>
							</SectionBlock>
							<SectionBlock
								id="target-section-permissions"
								label={t('__manage%_permision.multiple')}
								sectionKey="section-permissions"
								isOpen={expandedKeys.includes(
									'section-permissions'
								)}
								toggleSection={toggleSection}
							>
								<PermissionsInput
									data={lst}
									permissions={lstPermissions}
									type="lst"
									formIsDisabled={
										formIsDisabled ||
										!(isEditingSuperAdmin || isAdmin)
									}
									form={form}
								/>
							</SectionBlock>
							<SectionBlock
								id="target-section-categories"
								label={t('__manage%_category.multiple')}
								sectionKey="section-categories"
								isOpen={expandedKeys.includes(
									'section-categories'
								)}
								toggleSection={toggleSection}
							>
								<PermissionsInput
									data={expandedCategories}
									permissions={categoryPermissions}
									type="categories"
									formIsDisabled={
										formIsDisabled ||
										!(isEditingSuperAdmin || isAdmin)
									}
									form={form}
								/>
							</SectionBlock>
							<SectionBlock
								id="target-section-resources"
								label={t('__manage%_resource.multiple')}
								sectionKey="section-resources"
								isOpen={expandedKeys.includes(
									'section-resources'
								)}
								toggleSection={toggleSection}
							>
								<PermissionsInput
									data={resources}
									permissions={rolesAndResourcePermissions}
									type="resources"
									formIsDisabled={
										formIsDisabled ||
										!(isEditingSuperAdmin || isAdmin)
									}
									form={form}
								/>
							</SectionBlock>
						</div>
					</div>

					{/* Submit */}
					{!viewOnly && (
						<Row
							justify="end"
							gutter={[10, 10]}
							style={{ marginTop: 20 }}
						>
							<Col>
								<Form.Item style={{ marginBottom: 0 }}>
									<Button type="primary" htmlType="submit">
										{user.loaded === IS_NEW
											? t('single.create')
											: t('single.update')}
									</Button>
								</Form.Item>
							</Col>
							<Col>
								<Form.Item style={{ marginBottom: 0 }}>
									<Button
										type="default"
										onClick={() => form.resetFields()}
									>
										{t('single.reset')}
									</Button>
								</Form.Item>
							</Col>
						</Row>
					)}
				</Form>
			</ConfigProvider>
		</GeneralContentWrapper>
	)
}

UserEdit.propTypes = {
	viewOnly: PropTypes.bool
}

export default observer(UserEdit)
